import { ActionIcon, Box, Button, Drawer, Text, NoAccess } from "@dehub/theme";
import type { AsyncDataTableRef } from "@dehub/theme";
import { PlusIcon, XIcon } from "@phosphor-icons/react";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { IGeneral } from "@/models/General";
import { ReasonTooltip } from "@/shared/a11y/reason-tooltip";
import { MissingPermission, TransCode } from "@/shared/a11y/trans-code";
import type { BusinessPermissions } from "../permissions";
import type { BusinessRecord, BusinessType } from "../types";
import { StaffModal, type StaffFormValues } from "./StaffModal";
import { StaffTable, type IStaffRow } from "./StaffTable";
import classes from "../business.module.css";

type Props = Readonly<{
  opened: boolean;
  type: BusinessType;
  record: BusinessRecord;
  perms: BusinessPermissions;
  onClose: () => void;
}>;

export function BusinessUsersDrawer({ opened, type, record, perms, onClose }: Props) {
  const { t } = useTranslation("staff");
  const { t: tBusiness } = useTranslation("business");
  const { t: tCommon } = useTranslation("common");
  const accessLevels = useSelector((state: { general: IGeneral }) => state.general.accessLevels);
  const tableRef = useRef<AsyncDataTableRef | null>(null);
  const [modal, setModal] = useState<{ opened: boolean; editing: StaffFormValues | null }>({ opened: false, editing: null });

  const openAdd = useCallback(() => setModal({ opened: true, editing: null }), []);
  const openEdit = useCallback(
    (row: IStaffRow) => {
      const accessCode = row.user?.accessLevel ?? "";
      setModal({
        opened: true,
        editing: {
          userId: row.userId ?? "",
          roleIds: (row.user?.roles ?? []).map((role) => role.id),
          accessLevel: (accessLevels ?? []).some((a) => a.code === accessCode) ? accessCode : "",
        },
      });
    },
    [accessLevels],
  );

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="min(820px, 100%)"
      padding={0}
      withCloseButton={false}
      classNames={{ content: `${classes.tokens} ${classes.drawerRoot}`, body: classes.drawerBody }}
    >
      <Box className={classes.drawerHead}>
        <Box className={classes.drawerHeadRow}>
          <Box>
            <Text component="span" display="block" className={classes.drawerKicker}>
              {tBusiness(`typeFull.${type}`)} ·{" "}
              <Text component="span" className={classes.mono} translate="no">
                {record.refCode ?? "-"}
              </Text>
            </Text>
            <Drawer.Title className={classes.drawerTitle}>
              {t("user.drawerTitle")}
            </Drawer.Title>
          </Box>
          <Box className={classes.drawerFootGroup}>
            <ReasonTooltip reason={perms.userList.hasAccess ? null : <MissingPermission code={perms.codes.userList} />}>
              <Button leftSection={<PlusIcon size={14} weight="bold" />} color="brand" onClick={openAdd}>
                {t("user.addBtn")}
              </Button>
            </ReasonTooltip>
            <ActionIcon variant="subtle" color="gray" size={34} className={classes.closeButton} aria-label={tCommon("actions.close")} onClick={onClose}>
              <XIcon size={16} weight="bold" />
            </ActionIcon>
          </Box>
        </Box>
      </Box>

      <Box className={classes.drawerContent}>
        {perms.userList.hasAccess ? (
          <StaffTable ref={tableRef} businessId={record.id} onEdit={openEdit} onAdd={openAdd} />
        ) : (
          <NoAccess
            title={tBusiness("drawer.noAccessTitle")}
            description={<TransCode i18nKey="business:drawer.noAccessText" values={{ code: perms.codes.userList }} />}
          />
        )}
      </Box>

      <StaffModal
        opened={modal.opened}
        businessId={record.id}
        editing={modal.editing}
        onClose={() => setModal((m) => ({ ...m, opened: false }))}
        onSaved={() => tableRef.current?.reload()}
      />
    </Drawer>
  );
}
